/** TinyFish live-browser URLs we will iframe. Same allowlist as the BE. */

import { BROWSE_MAX_URLS } from "./browseModel";

export const BROWSE_WATCH_MAX_CHARS = 2048;

export type BrowseWatch = { host: string; url?: string };

function isWatchHost(host: string): boolean {
  const h = host.replace(/\.$/, "").toLowerCase();
  if (h === "tinyfish.ai" || h.endsWith(".tinyfish.ai")) return true;
  if (h.endsWith(".tinyfish.unikraft.app")) return true;
  // Production player (live GET /v1/runs): ip-{a-b-c-d}.tetra-data.production.tinyfish.io
  if (/^ip-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}\.tetra-data\.production\.tinyfish\.io$/.test(h)) {
    return true;
  }
  // Docs-era Unikraft player.
  return /^tf-[a-z0-9-]+\.[a-z0-9-]+-tinyfish\.unikraft\.app$/.test(h);
}

export function isBrowseWatchUrl(url: string): boolean {
  const trimmed = url.trim();
  if (trimmed.length === 0 || trimmed.length > BROWSE_WATCH_MAX_CHARS) {
    return false;
  }
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "https:") return false;
    if (u.port !== "") return false;
    if (u.username !== "" || u.password !== "") return false;
    if (!/^[a-z0-9.-]+$/i.test(u.hostname)) return false;
    return isWatchHost(u.hostname);
  } catch {
    return false;
  }
}

export function mergeBrowseWatch(
  existing: BrowseWatch[] | undefined,
  next: { host?: string; url?: string },
): BrowseWatch[] {
  const host = (next.host ?? "").trim();
  if (!host) return existing ?? [];
  const watch = existing ?? [];
  if (next.url && !isBrowseWatchUrl(next.url)) return watch;
  const i = watch.findIndex(
    (w) => w.host.toLowerCase() === host.toLowerCase(),
  );
  if (i >= 0) {
    const cur = watch[i];
    if (!cur) return watch;
    const url = next.url ?? cur.url;
    if (url === cur.url) return watch;
    const copy = watch.slice();
    copy[i] = { host: cur.host, url };
    return copy;
  }
  if (watch.length >= BROWSE_MAX_URLS) return watch;
  if (next.url && watch.some((w) => w.url === next.url)) return watch;
  return [...watch, { host, url: next.url }];
}

/** Drop live views for a site whose Agent run has finished. */
export function toggleWatchExpanded(
  current: string | null,
  clicked: string,
): string | null {
  return current === clicked ? null : clicked;
}

export function dropBrowseWatch(
  existing: BrowseWatch[] | undefined,
  host: string | undefined,
): BrowseWatch[] {
  const watch = existing ?? [];
  if (!host) return watch;
  const h = host.toLowerCase();
  return watch.filter((w) => w.host.toLowerCase() !== h);
}
